import { RouteEnum } from '@/constants/enums/RouteEnum';
import { env } from '@/env';
import type { Middleware } from '@/router/middlewarePipeline';
import { middlewarePipeline } from '@/router/middlewarePipeline';
import { fullRoutes } from '@/router/routes/';
import { baseFeatureRoutes } from '@/router/routes/baseFeatures';
import { paidFeatureRoutes } from '@/router/routes/paidFeatures';
import { setupLayouts } from 'virtual:generated-layouts';
import type {
  NavigationGuardNext,
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteRecordName,
  Router
} from 'vue-router';

import { useUserStore } from '@/stores/user';
import { createRouter, createWebHistory } from 'vue-router';

const scrollBehavior = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  savedPosition: any
) => (savedPosition ? savedPosition : { top: 0 });

// 暫存前一個路由名稱
export let previousRouteName: string | null = null;

// B2B 允許的路由前綴
const B2B_ALLOWED_PATH_PREFIXES: string[] = [
  '/live',
  '/homework-business',
  '/v2-live',
  '/v2-bookstore',
  '/v2-schedule',
  '/v2-calendar',
  '/v2-course-record',
  '/v2-collection',
  '/student-testimonials-sharing'
];

/**
 * 是否為 B2B 允許的路由
 * @param path - 路由路徑
 */
const isB2bAllowedPath = (path: string): boolean =>
  B2B_ALLOWED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

/**
 * 是否為 v2 版本會員允許的路由
 * @param version - 路由 meta 中的版本資訊
 */
const isV2AllowedRoute = (version: 'V1' | 'V2' | unknown): boolean => version === 'V2';

/**
 * 路由名稱類型判斷
 * @param name - 路由名稱
 */
const isRouteRecordName = (name: unknown): name is RouteRecordName =>
  typeof name === 'string' || typeof name === 'symbol';

// 功能頁面路由名稱集合（基礎 + 付費）
const featureRouteNameSet: Set<RouteRecordName> = new Set(
  [...baseFeatureRoutes, ...paidFeatureRoutes].map((route) => route.name).filter(isRouteRecordName)
);

/**
 * 路由名稱是否在功能頁面路由名稱集合中
 * @param name - 路由名稱
 * @param set - 路由名稱集合
 */
const isInNameSet = (name: RouteRecordName | null | undefined, set: Set<RouteRecordName>) => {
  if (name == null) return false;
  return set.has(name);
};

type UserStore = ReturnType<typeof useUserStore>;

type RunRouteMiddlewareParams = {
  middleware: Middleware | Middleware[];
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  router: Router;
};

/**
 * 執行路由中介
 * @param params - 參數物件
 * @param params.middleware - 中介函式或中介函式陣列
 * @param params.to - 目標路由位置
 * @param params.from - 來源路由位置
 */
const runRouteMiddleware = ({
  middleware,
  to,
  from,
  router
}: RunRouteMiddlewareParams): Promise<NavigationGuardReturn | void> =>
  new Promise<NavigationGuardReturn | void>((resolve) => {
    const next: NavigationGuardNext = (arg?) => resolve(arg as NavigationGuardReturn | void);
    middlewarePipeline(middleware, to, from, next, router);
  });

/**
 * 路由其他存取規則
 * @param params - 參數物件
 * @param params.toName - 目標路由名稱
 * @param params.userStore - 使用者狀態儲存庫
 */
const applyRouteAccessRules = (params: {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  toName: RouteRecordName | null | undefined;
  userStore: UserStore;
}): NavigationGuardReturn | void => {
  const { to, toName, userStore } = params;

  if (!userStore.isLogin) return;
  if (!userStore.hasUserInfo) return;

  // B2B 會員路由存取限制
  if (userStore.isB2bUser && !isB2bAllowedPath(to.path)) {
    // 直接呼叫登出
    void userStore.logout();
    return false;
  }

  // === 站內功能頁面存取規則 ===
  const shouldCheckFeatureEntry =
    toName === RouteEnum.Index || isInNameSet(toName, featureRouteNameSet);

  if (shouldCheckFeatureEntry) {
    /**
     * 功能頁面需完成初始設定後才能進入
     * @remarks 重新導向至對應的設定頁面
     */
    const hasLevel = Boolean(userStore.userData?.hasLevel);
    if (!hasLevel) {
      return { name: RouteEnum.ProfileSetup, params: { settingsType: 'level' } };
    }

    if (userStore.isB2bUser) return;

    const themeSelection = userStore.userData?.themeSelection ?? [];
    if (themeSelection.length === 0) {
      return { name: RouteEnum.ProfileSetup, params: { settingsType: 'theme' } };
    }
  }

  // == v2 與 v1 會員路由互斥限制 ==
  // 標記 allowAllVersions 的路由 v1、v2 會員皆可造訪
  if (!to.meta?.allowAllVersions) {
    const isV2Route = isV2AllowedRoute(to.meta?.version);

    // v2 會員路由存取限制
    if (userStore.isV2OfficialUser && !isV2Route) {
      window.location.href = env.officialV2Url;
      return false;
    }

    // v1 會員路由存取限制
    if (!userStore.isV2OfficialUser && isV2Route) {
      return { name: RouteEnum.Index };
    }
  }

  return;
};

/**
 * 初始化路由實例
 */
export const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: setupLayouts(fullRoutes),
    scrollBehavior
  });

  /**
   * 全域前置守衛 - Middleware + 其他邏輯
   * @remarks 在路由切換前驗證使用者權限、裝置判斷、廣告參數處理等，並處理登入後的導頁規則
   */
  router.beforeEach(async (to, from) => {
    const userStore = useUserStore();
    const toName = to.name;

    // 記錄前一個路由名稱
    previousRouteName = typeof from.name === 'string' ? from.name : null;

    // 1. 路由權限中介
    const middleware = to.meta.middleware as Middleware | Middleware[] | undefined;
    if (middleware) {
      const middlewareResult = await runRouteMiddleware({ middleware, to, from, router });
      if (middlewareResult !== undefined) return middlewareResult;
    }

    // 2. 路由其他存取規則
    return applyRouteAccessRules({ to, from, toName, userStore });
  });

  // router.afterEach((to, from, next) => {
  //   //學習建議在註冊後，跳轉到訂閱頁，滚動條消失
  //   //要換個方法處理
  //   // document.querySelector('body')?.setAttribute('style', 'overflow: auto !important;');
  // });

  return router;
};
